import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { endpoints } from "@/core/api/endpoints";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import type {
  ContributionInitiateRequestDto,
  ContributionTransactionDto,
} from "@/features/fundraising/api/dto";

/**
 * Initie une contribution à une cagnotte (`POST /Transactions/initiate`,
 * wallet-service §5.3) avec `type = POT_CONTRIBUTION` (annexe A) — opéré
 * par le back-office pour le compte du client.
 *
 * Idempotency-Key OBLIGATOIRE (§4.7) : clé persistée par empreinte du
 * corps ; 409 = attendre puis rejouer ; 422 = nouvelle tentative.
 *
 * Réplique le hook `useInitiateTransaction` de wallet via le registre
 * `@/core/api/endpoints` (règle : pas d'import croisé entre features).
 */
export const useInitiateContribution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["fundraising", "contributions", "initiate"],
    mutationFn: (body: ContributionInitiateRequestDto) =>
      mutateIdempotent<ContributionTransactionDto>({
        scopeId: "fundraising.contributions.initiate",
        url: endpoints.wallet.transactions.initiate,
        body,
      }),
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        walletTransactionCacheKeys(),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useInitiateContribution failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fundraising" }
      );
    },
  });
};
